import { Tag } from '../api/types.js';
import { listTagImplications, listTags } from '../api/index.js';

/**
 * Singleton cache for the tag-implication graph — same lazy-singleton
 * shape as lib/unit-cache.ts's UnitCache. Storage stays normalized (only
 * explicit tags are ever written to an ingredient); this is what lets
 * every chip-rendering site compute the *implied* tags to show alongside
 * the explicit ones, purely client-side.
 */
class TagGraphCache {
  private tagsById: Map<string, Tag> = new Map();
  private forwardEdges: Map<string, Set<string>> = new Map();
  private loaded = false;
  private loadPromise: Promise<void> | null = null;

  async ensureLoaded(): Promise<void> {
    if (this.loaded) return;
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = this.fetch().then(() => {
      this.loaded = true;
      this.loadPromise = null;
    }).catch(err => {
      this.loadPromise = null;
      throw err;
    });

    return this.loadPromise;
  }

  private async fetch(): Promise<void> {
    const [tags, implications] = await Promise.all([listTags(), listTagImplications()]);
    this.tagsById = new Map(tags.map(t => [t.id, t]));
    this.forwardEdges = new Map();
    for (const imp of implications) {
      if (!this.forwardEdges.has(imp.fromTagId)) {
        this.forwardEdges.set(imp.fromTagId, new Set());
      }
      this.forwardEdges.get(imp.fromTagId)!.add(imp.toTagId);
    }
  }

  /**
   * Every tag transitively implied by any of `explicitTags` — BFS over
   * the cached edges, excluding tags already in `explicitTags` — resolved
   * to full Tag objects (for rendering name/color). Callers render these
   * alongside the explicit ones, dimmed. Returns [] before ensureLoaded()
   * resolves, so a caller mid-load just shows explicit tags until the
   * follow-up render (same "explicit now, implied once ready" pattern
   * used everywhere this is called).
   */
  getImpliedTags(explicitTags: Tag[]): Tag[] {
    const visited = new Set(explicitTags.map(t => t.id));
    const result: Tag[] = [];
    const stack = [...visited];
    while (stack.length > 0) {
      const current = stack.pop()!;
      for (const implied of this.forwardEdges.get(current) ?? []) {
        if (!visited.has(implied)) {
          visited.add(implied);
          const tag = this.tagsById.get(implied);
          if (tag) result.push(tag);
          stack.push(implied);
        }
      }
    }
    return result;
  }

  /** Mark the cache as stale so it's re-fetched on next access — call
   *  after any write to the implication graph itself (setTagImplications,
   *  deleteTag). */
  invalidate(): void {
    this.loaded = false;
  }
}

export const tagGraphCache = new TagGraphCache();
